/**
 * Client per il motore Playtomic (multi-account) montato sotto /api/ptapp/*.
 * Dev: Vite proxy /api -> :8002 (staging). Prod: stesso origin (router nel CC).
 * Auth: HTTP Basic gestita dal browser (come il resto del Control Center).
 */
const MOVING_TENANT = "dcf3836c-6f75-43d6-af7b-b99a265be1f5";

export type Account = { key: string; email: string; user_id: string; name?: string };

async function j<T>(url: string, opts: RequestInit = {}): Promise<T> {
  const r = await fetch(url, opts);
  if (!r.ok) throw new Error("HTTP " + r.status + ": " + (await r.text()).slice(0, 200));
  if (r.status === 204) return null as T;
  const t = await r.text();
  return (t ? JSON.parse(t) : null) as T;
}

export const ptapp = {
  MOVING_TENANT,

  accounts: () => j<Account[]>("/api/ptapp/accounts"),
  uid: (acct: string) => j<{ uid: string }>(`/api/ptapp/uid/${acct}`),

  /** Proxy generico verso Playtomic. host = app | api | mgr | apiapp */
  call<T = unknown>(
    acct: string,
    host: "app" | "api" | "mgr" | "apiapp",
    path: string,
    opts: { method?: string; body?: unknown; params?: Record<string, string | number | boolean>; scope?: string } = {},
  ): Promise<T> {
    let url = `/api/ptapp/call/${acct}/${host}/${path}`;
    if (opts.params) {
      const q = new URLSearchParams(
        Object.fromEntries(Object.entries(opts.params).map(([k, v]) => [k, String(v)])),
      ).toString();
      if (q) url += "?" + q;
    }
    const headers: Record<string, string> = {};
    if (opts.body) headers["Content-Type"] = "application/json";
    if (opts.scope) headers["X-Authorization-Scope"] = opts.scope;
    return j<T>(url, {
      method: opts.method || "GET",
      headers,
      body: opts.body ? JSON.stringify(opts.body) : undefined,
    });
  },

  // chat
  threads: (acct: string) => j<ChatThread[]>(`/api/ptapp/chat/${acct}/threads`),
  msgs: (acct: string, chatId: string) => j<ChatMsg[]>(`/api/ptapp/chat/${acct}/msgs/${encodeURIComponent(chatId)}`),
  sendMsg: (acct: string, chatId: string, text: string) =>
    j<{ ok: boolean; id: string }>(`/api/ptapp/chat/${acct}/send`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ chat_id: chatId, text }),
    }),
  names: (acct: string, ids: string[]) =>
    j<Record<string, { name: string; picture: string | null }>>(
      `/api/ptapp/chat/${acct}/names?ids=${encodeURIComponent(ids.join(","))}`),
  engaged: (acct: string, ids: string[]) =>
    j<Record<string, boolean | null>>(
      `/api/ptapp/chat/${acct}/engaged?ids=${encodeURIComponent(ids.join(","))}`),
};

export type ChatThread = {
  chat_id: string; name?: string; type?: string; resource?: string; sport_id?: string;
  match_date?: number; users?: string[]; last_text?: string | null; last_date?: number;
  last_user_id?: string | null; last_is_system?: boolean;
};
export type ChatMsg = { id: string; user_id?: string; date?: number; data?: { text?: string; type?: string; category?: string } };
